import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export default function Lightbox({ photos, index, onClose, onNav, title }) {
  const open = index !== null && !!photos && photos.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    if (window.__lenis) window.__lenis.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open, onClose, onNav]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-lg flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-5 right-5 z-10 w-11 h-11 rounded-full border border-white/20 bg-ink/60 text-cream flex items-center justify-center hover:bg-cream hover:text-ink transition-colors"
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={20} />
          </button>

          <button
            className="absolute left-3 md:left-8 z-10 w-11 h-11 rounded-full border border-white/20 bg-ink/60 text-cream flex items-center justify-center hover:bg-cream hover:text-ink transition-colors"
            onClick={(e) => { e.stopPropagation(); onNav(-1); }}
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={22} />
          </button>

          <button
            className="absolute right-3 md:right-8 z-10 w-11 h-11 rounded-full border border-white/20 bg-ink/60 text-cream flex items-center justify-center hover:bg-cream hover:text-ink transition-colors"
            onClick={(e) => { e.stopPropagation(); onNav(1); }}
            aria-label="Next photo"
            data-testid="lightbox-next"
          >
            <ChevronRight size={22} />
          </button>

          <motion.img
            key={index}
            src={photos[index]}
            alt={`Gallery photo ${index + 1}`}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[80vh] max-w-[88vw] md:max-w-[82vw] object-contain rounded-2xl shadow-2xl shadow-black/60"
            onClick={(e) => e.stopPropagation()}
            data-testid="lightbox-image"
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center" data-testid="lightbox-counter">
            <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-sage">
              {index + 1} / {photos.length}
            </p>
            {title && <p className="font-display italic text-lg text-cream/80 mt-1 px-6">{title}</p>}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}